import React from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  PopoverTrigger,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import {pluralize} from '../../lib/helpers';
import {Campaign} from '../../shared/chatabm';

const columns: ColumnDef<Campaign>[] = [
  {
    accessorKey: 'name',
    header: 'Campaign Name',
    cell: ({row}) => <Text fontWeight="500">{row.getValue('name')}</Text>,
  },
  {
    accessorKey: 'examples',
    header: 'Examples',
    cell: ({row}) => {
      const examples = row.getValue('examples') as string[];
      return (
        <Text color="kgray.400" lineHeight="1.4">
          {examples.join(', ')}
        </Text>
      );
    },
  },
  {
    accessorKey: 'target',
    header: 'Target',
    cell: ({row}) => <Text>{row.getValue('target')}</Text>,
  },
  {
    accessorKey: 'brief',
    header: 'Brief',
    cell: ({row}) => {
      // Use a popover to show the brief since it takes up a lot of space
      return (
        <>
          <Popover>
            <PopoverTrigger>
              <Button size="sm" variant="outline">
                View Brief
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverBody>
                <Text>{row.getValue('brief')}</Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </>
      );
    },
  },
];

export const CampaignsTable = ({campaigns}: {campaigns: Campaign[]}) => {
  const table = useReactTable({
    data: campaigns,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box px={6} py={4} overflowY="auto" h="100%">
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="500">
            {campaigns.length > 0
              ? `${pluralize(campaigns.length, 'Campaign', 'Campaigns')}`
              : 'No campaigns generated yet.'}
          </Text>
        </Flex>

        <TableContainer
          maxW="100%"
          overflowX="auto"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
        >
          <Table variant="simple" size="sm" minW="600px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      bg="kgray.100"
                      color="kgray.400"
                      fontSize="xs"
                      py={3}
                      px={4}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      py={3}
                      px={4}
                      lineHeight="1.4"
                      whiteSpace="normal"
                      verticalAlign="top"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </VStack>
    </Box>
  );
};
